import { useNavigate } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, PauseCircle, Loader2, Settings } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useOutreachEngineStatus } from '@/hooks/useOutreachEngineStatus'
import { useCanDo } from '@/hooks/usePermissions'

// ────────────────────────────────────────────────────────────────────────
// EngineStatusBanner — an HONEST banner about the outreach executor.
//
// The engine is OFF by owner decision. Cadence surfaces used to imply that
// "active" cadences were sending; this banner tells the truth: scheduled
// steps are NOT executing while the engine is off. It reads real state and
// only LINKS to the admin toggle at /settings/outreach — it never toggles the
// switch or touches crons itself (nothing here can reactivate the engine).
//
// This is the minimal inline banner requested for the Cadence Flow rail; the
// full-featured EngineStatusBanner in src/components/outreach is owned by
// another rail and intentionally not imported here to avoid a hard dependency.
// ────────────────────────────────────────────────────────────────────────

type Props = {
  /** Compact single-line variant for dense surfaces (e.g. the builder header). */
  compact?: boolean
  className?: string
}

export function EngineStatusBanner({ compact = false, className }: Props) {
  const navigate = useNavigate()
  const { status, isLoading, isError, engineOn, engineOff } = useOutreachEngineStatus()
  const canManage = useCanDo('outreach_admin')

  // Loading — don't assert anything about the engine yet.
  if (isLoading) {
    return (
      <Card
        className={`flex items-center gap-2 border-dashed bg-muted/30 px-4 py-2 text-xs text-muted-foreground ${className ?? ''}`}
      >
        <Loader2 className="size-3.5 animate-spin" />
        Comprobando el estado del motor de outreach…
      </Card>
    )
  }

  // Error — be honest that we couldn't confirm, don't imply it's running.
  if (isError || !status) {
    return (
      <Card
        className={`flex items-center gap-2 border-amber-500/40 bg-amber-500/5 px-4 py-2 text-xs text-amber-700 dark:text-amber-400 ${className ?? ''}`}
      >
        <AlertTriangle className="size-3.5 shrink-0" />
        No se pudo confirmar el estado del motor de outreach. Trátalo como apagado hasta verificar.
      </Card>
    )
  }

  // Engine ON — running for real.
  if (engineOn) {
    if (compact) {
      return (
        <span
          className={`inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 ${className ?? ''}`}
        >
          <CheckCircle2 className="size-3.5" />
          Motor activo
        </span>
      )
    }
    return (
      <Card
        className={`flex items-center gap-2 border-emerald-500/40 bg-emerald-500/5 px-4 py-2 text-xs text-emerald-700 dark:text-emerald-400 ${className ?? ''}`}
      >
        <CheckCircle2 className="size-3.5 shrink-0" />
        <span>
          El motor de outreach está <strong>encendido</strong>: los pasos agendados se ejecutan
          según su horario.
        </span>
      </Card>
    )
  }

  // Engine OFF — the important, honest case.
  const orphans = status.orphan_scheduled_count
  const orphanNote =
    engineOff && orphans > 0
      ? ` Hay ${orphans} paso${orphans === 1 ? '' : 's'} agendado${orphans === 1 ? '' : 's'} vencido${orphans === 1 ? '' : 's'} que no se ejecutará${orphans === 1 ? '' : 'n'} mientras el motor esté apagado.`
      : ''

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-xs font-medium text-muted-foreground ${className ?? ''}`}
      >
        <PauseCircle className="size-3.5" />
        Motor apagado
      </span>
    )
  }

  return (
    <Card
      className={`flex flex-wrap items-center gap-2 border-amber-500/40 bg-amber-500/5 px-4 py-2.5 text-xs text-amber-800 dark:text-amber-300 ${className ?? ''}`}
    >
      <PauseCircle className="size-4 shrink-0" />
      <span className="min-w-0 flex-1">
        El motor de outreach está <strong>apagado</strong>. Las cadencias pueden verse “activas”,
        pero los pasos agendados <strong>no se ejecutan</strong>.{orphanNote}
      </span>
      {canManage && (
        <Button
          variant="outline"
          size="sm"
          className="h-7 border-amber-500/50 text-amber-800 hover:bg-amber-500/10 dark:text-amber-300"
          onClick={() => navigate('/settings/outreach')}
        >
          <Settings className="mr-1 size-3.5" />
          Gestionar motor
        </Button>
      )}
    </Card>
  )
}
